import React from 'react'
import styled from '@emotion/styled'
import { IconName, Icon } from '@mobi/component-library/Common/V2/Icon/Icon'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { hexColors } from '@mobi/settings/hexColors'

type Props = {
  title: React.ReactNode
  style?: Partial<React.CSSProperties>
} & (
  | {
      rightAlignedComponent?: React.ReactNode
      iconName?: never
      onIconSelect?: never
      isSelected?: never
      isDisabled?: never
    }
  | {
      /** Right aligned button icon */
      iconName?: IconName
      onIconSelect?: (selected: boolean) => void
      isSelected?: boolean
      isDisabled?: boolean
      rightAlignedComponent?: never
    }
)

export const Header: React.FC<Props> = ({
  title,
  iconName,
  onIconSelect,
  isSelected,
  isDisabled,
  style,
  rightAlignedComponent,
}) => {
  const handleButtonClick = () => {
    onIconSelect?.(!isSelected)
  }

  return (
    <HeaderStyled style={style}>
      <h2>{title}</h2>

      {iconName && (
        <Button
          isIconOnlyButton
          color='tertiary_color'
          disabled={isDisabled}
          onClick={handleButtonClick}
        >
          <Icon name={iconName} color={colors.white} />
        </Button>
      )}

      {rightAlignedComponent ? rightAlignedComponent : null}
    </HeaderStyled>
  )
}

// ======
// Styles
// ======

const HeaderStyled = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacing.smx2,
  minHeight: '5.2rem',
  padding: `0 ${spacing.md}`,

  background: colors.lavender[750],
  color: hexColors.white,

  '> h2': {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'unset',
    color: colors.white,
    fontWeight: font.weight.semibold,
    fontFamily: font.family.primary,
    fontSize: font.size.xl2.fontSize,
    lineHeight: font.size.xl2.lineHeight,
    letterSpacing: font.size.xl2.letterSpacing,
  },

  '> a, > button': {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minWidth: '4rem',
  },

  '> a': {
    textDecoration: 'none',
    color: 'unset',
    userSelect: 'none',
    transition: 'opacity 0.25s ease-out',
    WebkitTapHighlightColor: 'transparent',

    '@media (hover: hover) and (pointer: fine)': { ':hover': { opacity: 0.75 } },
    ':active': { opacity: 0.5 },
  },
})
